import React, { useState, useEffect, useContext, useRef } from "react";
import { Stack } from "@fluentui/react";
import DocumentBasicItem from "../../../components/checkinDocuments/DocumentBasicItem";
import SuccessDialog from "../../../components/SuccessDialog";
import ErrorDailog from "../../../components/ErrorDailog";
import MessageBox from "../../../components/MessageBox";
import SuccessToastBox from "../../../components/toasts/SuccessToastBox";
import ReusableDialog from "../../../components/popupModals/ReusableDialog";
import MessageBoxContent from "../../../components/popupModals/MessageBoxContent";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_ROUTES, NAV_LOCATION_STATE } from "../../../routes/routeConfig";
import { filterHierarchy } from "../../../functions/filterHierarchy";
import { cleanAll } from "../../../components/checkinDocuments/ClassFormatTreeLoad";
import { resetHierarchy } from "../../../functions/resetHierarchy";
import { useTeamsRequestContext } from "../../../redux/hooks/useTeamsRequestContext";
import {
  CHECKIN_NEW_PAGE_NAME,
  DATA_LOAD_STATUS,
  CHECKIN_PROCESS_TYPE,
  INVALID_DOCUMENT,
  PAGE_NAME,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  DOC_CHECK_IN_TYPE,
  DOCUMENT_LOAD_STATUS,
} from "../../../redux/constants/metaData";
import { useCommonControlsStyle } from "../../../theme/styles/CommonControlsStyle";
import { useTheme } from "../../../theme/OAS365Theme";
import { useCheckinPanelStyle } from "../../../theme/styles/CheckinPanelStyle";
import { TeamsFxContext } from "../../../components/Context";
import { useCheckin } from "../../../redux/hooks/useCheckin";
import { useCheckinDocument } from "../../../redux/hooks/useCheckinDocument";
import { useMetadata } from "../../../redux/hooks/useMetadata";
import { useLogin } from "../../../redux/hooks/useLogin";
import { useOneDrive } from "../../../redux/hooks/useOneDrive";
import { useDocuments } from "../../../redux/hooks/useDocuments";
import { useTools } from "../../../redux/hooks/useTools";
import { resolveGroupId } from "../../check-in-new-list/checkInNewList.utils";
import { isValidPlayload } from "../../../functions/reduxMethod";
import {
  isFileExtValid,
  isWorkingDocumentDownloaded,
} from "../../../functions/helperMethod";
import {
  CHECKIN_NEW_TIME_OUT,
  SUCCESS_TYPE,
} from "../../../redux/constants/messageTypes";
import CheckInNewFooterActions from "./CheckInNewFooterActions";
import {
  DISPLAY_TEXT,
  TAB_KEY,
  DATA_LOADING_STATUS,
  CHECKIN_LOADING_MESSAGE,
  ONEDRIVE_UPDATE_MESSAGE,
} from "./checkInNew.constants";
import {
  shouldTriggerRefreshTokenPreAction,
  isCheckinInLoadingState,
} from "./checkInNew.helpers";
import { logger } from "../../../utils/logger";
import { useWorkingDocument } from "../../../redux/hooks/useWorkingDocument";
import { useAppDispatch } from "../../../redux/hooks";
import { ON_ATTACHED_DOC_INFO } from "../../../redux/constants/actionTypes";
import { updateOneDriveMetadata } from "../../../redux/api/documentsAPI";
import { requestRefreshCheckInNewList } from "../../../redux/slices/oneDrive/oneDriveDocsListSlice";
import UserAuth from "../../../auth/userAuth";
import DeleteOneDriveCheckbox from "../../check-in-new-revision/components/DeleteOneDriveCheckbox";
import { useIFSBrowseAutopopulation } from "../../../redux/hooks/useIFSBrowseAutopopulation";

const CheckInNew = () => {
  const dispatch = useAppDispatch();
  const checkin = useCheckin();
  const { checkinDocument: submitCheckin, checkinLoading } =
    useCheckinDocument();
  const metadata = useMetadata();
  const { teamsContext } = useLogin();
  const { deleteOneDriveDocument, fetchOneDriveDownloadUrl } = useOneDrive();
  const { ifsLatestDocument } = useDocuments();
  const tools = useTools();
  const { refreshWorkingDocuments } = useWorkingDocument();

  const {
    isAutopopulating: isIFSAutopopulating,
    isAutopopulated: isIFSAutopopulated,
  } = useIFSBrowseAutopopulation();

  const {
    docTitle,
    docClass,
    docFormat,
    viewRequired,
    formatRequired,
    objConRequired,
    restrictedAccess,
    uploadFiles,
    uploadViewCopyCount,
    viewCopyFiles,
    docClassFormatList,
    createViewCopy,
    selectedObjectConnections,
    recentObjectsSelectedDecription,
    attachedDocInfo,
    checkinDocumentProgress,
    docFileTypeList,
    docReleaseStatus,
  } = checkin;
  const fileCount = checkin.uploadFileCount;
  const {
    advObjCompleted,
    checkinPageName,
    docCheckinType,
    searchAdvanceObjectSelected,
    activePage,
    objectConnectionDataLoad,
    tokenRefreshPreActionInvoke,
  } = metadata;
  // NOTE: we compute `oneDriveDocumentDownloadUrl` later after `useLocation()`
  // so we can prefer `location.state.selectedDocumentDownloadUrl` when coming from the check-in list.
  const {
    tenantId: requestTenantId,
    groupId: requestGroupId,
    channelId: requestChannelId,
  } = useTeamsRequestContext();
  const { checkinSuccess, oneDriveDocumentId, oneDriveDeleteRequired } =
    ifsLatestDocument || {};

  const {
    onCheckinViewCopy,
    onGetObjectConnectionColumns,
    onObjectConnectionsLoad,
    getRecentObjects,
  } = checkin;
  const {
    onDocTitleChange,
    onDocClassFormatChange,
    onFileUploadChange,
    onFileDataChange,
    onDocReleaseChange,
    onViewCopyUploadChange,
    onDocClassFormatListChange,
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onSelectedObjectConnectionChanged,
    onRecentObjectConnectionChanged,
    onRecentObjectDataLoadStatusChanged,
    onBasicValidation,
    onCheckinValidation,
    onSelectedKeyLuNameChange,
    onAdvObjCompeleted,
    onCheckinPageNameChanged,
    onRecentObjectSelectedDescription,
    onOneDriveDataLoad,
    onDocCheckinTypeChange,
    onObjectConnectionDataLoad,
    onCheckinDocumentProgress,
    onCheckinProgressChange,
    onAdvObjSearchModelSelected,
    onAllowSelectionChange,
    onSetDocumentToDeleteOneDrive,
    onCheckinNewErrorTrigger,
    onBasicDataProgressChange,
    onSuccessMessageInvoke,
  } = tools;

  const displayText = DISPLAY_TEXT;

  const { themeString } = useContext(TeamsFxContext);
  const [isTreeDataLoading, setIsTreeDataLoading] = useState(false);
  const [isCheckinInProgress, setIsCheckinInProgress] = useState(false);
  const [isUpdatingOneDrive, setIsUpdatingOneDrive] = useState(false);
  // Lets the user dismiss the check-in progress popup without cancelling the
  // background check-in; navigates back to where the check-in was launched from.
  const [progressDialogDismissed, setProgressDialogDismissed] = useState(false);
  // Ref mirror so the long-running check-in async flow can read the latest value
  // (its closure captured the state at start) to skip the post-success navigation.
  const progressDialogDismissedRef = useRef(false);
  const [showFieldValidationErrors, setShowFieldValidationErrors] =
    useState(true);
  const tabKey = TAB_KEY;

  const styleTheme = useCommonControlsStyle();
  const panelStyle = useCheckinPanelStyle();

  // useEffect(() => {
  //   if (checkinSuccess && checkinType === DOC_CHECK_IN_TYPE.checkinNew) {
  //     const groupId =
  //       teamsContext?.channel?.membershipType === "Private"
  //         ? teamsContext?.channel?.ownerGroupId
  //         : teamsContext?.team?.groupId;

  //     if (oneDriveDeleteRequired && oneDriveDocumentId) {
  //       onDeleteOneDriveDocument(
  //         teamsContext?.channel?.membershipType,
  //         oneDriveDocumentId,
  //         groupId,
  //         null,
  //         DOC_CHECK_IN_TYPE.checkinNew,
  //       );
  //     }

  //     onSetDocumentToDeleteOneDrive({
  //       checkinSuccess: false,
  //       id: null,
  //       deleteRequired: false,
  //       checkinType: null,
  //     });
  //     onAllowSelectionChange(false);
  //   }
  // }, [
  //   checkinSuccess,
  //   oneDriveDeleteRequired,
  //   checkinType,
  //   teamsContext,
  //   oneDriveDocumentId,
  //   onDeleteOneDriveDocument,
  //   onSetDocumentToDeleteOneDrive,
  //   onAllowSelectionChange,
  // ]);

  // useEffect(() => {
  //   if (
  //     docCheckinType === CHECKIN_NEW &&
  //     attachedDocInfo !== null &&
  //     attachedDocInfo !== INVALID_DOCUMENT
  //   ) {
  //     if (uploadViewCopyCount > 0) {
  //       onCheckinViewCopy(
  //         docTitle,
  //         docClass,
  //         attachedDocInfo.docNo,
  //         attachedDocInfo.docSheet,
  //         attachedDocInfo.docRev,
  //         viewCopyFiles,
  //         viewRequired,
  //         docReleaseStatus,
  //         docCheckinType,
  //         false,
  //         {
  //           retry: 0,
  //           actionType: REFRESH_TOKEN_ACTION.checkinNewViewCopy,
  //           refreshResponse: RESPONSE_TYPE.none,
  //         },
  //         REFRESH_TOKEN_ACTION.checkinNewViewCopy
  //       );
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [attachedDocInfo]);

  useEffect(() => {
    if (checkinPageName === CHECKIN_NEW_PAGE_NAME.file) {
      setNextButtonVisibility(true);
      setCheckinButtonVisibility(false);
      setObjectPanelVisible(false);
      setCancelButtonVisibility(true);
    } else if (checkinPageName === CHECKIN_NEW_PAGE_NAME.basic) {
      setNextButtonVisibility(false);
      setCheckinButtonVisibility(true);
      setObjectPanelVisible(false);
      setCancelButtonVisibility(true);
      moveToObjectConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkinPageName]);

  useEffect(() => {
    if (advObjCompleted) {
      onPovotItemClick();
      onAdvObjCompeleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advObjCompleted]);

  useEffect(() => {
    if (searchAdvanceObjectSelected) {
      moveToObjectConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAdvanceObjectSelected]);

  const navigate = useNavigate();
  const location = useLocation();
  // Only use the URL from navigation state. Do not fall back to Redux `downloadUrl`:
  // it is not keyed by document id and can still hold the *previous* file's URL when
  // the list has no `downloadlink`, which caused wrong bytes on a later check-in.
  const oneDriveDocumentDownloadUrl =
    location.state?.selectedDocumentDownloadUrl ?? null;
  const selectedDocument = location.state?.selectedDocument ?? null;

  const handleDeleteOneDriveChange = (event, checked) => {
    const selectedId = oneDriveDocumentId ?? selectedDocument?.key;
    if (!selectedId) {
      return;
    }
    onSetDocumentToDeleteOneDrive({
      checkinSuccess: false,
      id: selectedId,
      deleteRequired: checked,
      checkinType: DOC_CHECK_IN_TYPE.checkinNew,
    });
  };

  useEffect(() => {
    if (!selectedDocument) {
      return;
    }
    onDocCheckinTypeChange(DOC_CHECK_IN_TYPE.checkinNew);

    // Initialize class and format from the selected revision item (set when coming from working documents)
    const revItem = location.state?.selectedRevisionItem;
    if (revItem?.class) {
      onDocClassFormatChange(
        revItem.class,
        revItem.format || "",
        "N", // Default requirements to 'N'; they will be updated by the metadata fetch
        "N",
        "N",
        null,
        fileCount,
      );
    }

    onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, fileCount, location.state?.selectedRevisionItem]);

  useEffect(() => {
    if (docCheckinType === DOC_CHECK_IN_TYPE.checkinNew) {
      switch (checkinDocumentProgress) {
        case DOCUMENT_LOAD_STATUS.onCompletion:
          setIsCheckinInProgress(false);
          setIsUpdatingOneDrive(false);
          onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
          onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
          onClear(false);
          // Skip the navigation when the user dismissed the progress popup.
          if (!progressDialogDismissedRef.current) {
            navigate(APP_ROUTES.allDocuments);
          }
          break;
        case DOCUMENT_LOAD_STATUS.onViewCopyUpload:
          if (
            isValidPlayload(attachedDocInfo) === true &&
            attachedDocInfo !== INVALID_DOCUMENT
          ) {
            if (uploadViewCopyCount > 0) {
              const isBrowseSource = selectedDocument?.source === "browse";
              onCheckinViewCopy(
                docTitle,
                docClass,
                attachedDocInfo.docNo,
                attachedDocInfo.docSheet,
                attachedDocInfo.docRev,
                viewCopyFiles,
                viewRequired,
                docReleaseStatus,
                docCheckinType,
                !isBrowseSource && oneDriveDeleteRequired,
                {
                  retry: 0,
                  actionType: REFRESH_TOKEN_ACTION.checkinNewViewCopy,
                  refreshResponse: RESPONSE_TYPE.none,
                },
                REFRESH_TOKEN_ACTION.checkinNewViewCopy,
              );
            }
          }
          break;
        case DOCUMENT_LOAD_STATUS.onDeleting:
          const { groupId: deletingGroupId, fileRoot: deletingFileRoot } =
            resolveGroupId(teamsContext);
          const effectiveDeletingGroupId =
            deletingGroupId || requestGroupId || "";
          const deletingMembershipType =
            teamsContext?.channel?.membershipType || "Regular";
          const deletingChannelId =
            teamsContext?.channel?.id || requestChannelId || "";
          const isBrowseSourceForDelete = selectedDocument?.source === "browse";

          (async () => {
            try {
              // Browse files are local — their key is not a Graph drive item id.
              if (
                !isBrowseSourceForDelete &&
                oneDriveDeleteRequired &&
                oneDriveDocumentId
              ) {
                await deleteOneDriveDocument(
                  deletingMembershipType,
                  oneDriveDocumentId,
                  effectiveDeletingGroupId,
                  null,
                  DOC_CHECK_IN_TYPE.checkinNew,
                  deletingFileRoot,
                  deletingChannelId,
                );
              }
            } finally {
              // Keep both "Shared" (working documents) and "Check In New" list in sync.
              try {
                await refreshWorkingDocuments(50);
              } catch (_e) {}

              onSetDocumentToDeleteOneDrive({
                checkinSuccess: false,
                id: null,
                deleteRequired: false,
                checkinType: null,
              });
              onAllowSelectionChange(false);
              setIsCheckinInProgress(false);
              setIsUpdatingOneDrive(false);
              onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
              onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
              onClear(false);
              // Skip the navigation when the user dismissed the progress popup.
              if (!progressDialogDismissedRef.current) {
                navigate(APP_ROUTES.allDocuments);
              }
            }
          })();
          break;
        case DOCUMENT_LOAD_STATUS.onError:
          setIsCheckinInProgress(false);
          setIsUpdatingOneDrive(false);
          onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
          onAllowSelectionChange(false);
          onCancel();
          break;
        default:
          break;
      }
    }
  }, [checkinDocumentProgress, navigate]);

  useEffect(() => {
    if (
      shouldTriggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) === true
    ) {
      const run = async () => {
        switch (tokenRefreshPreActionInvoke.actionType) {
          case REFRESH_TOKEN_ACTION.checkinNew:
            await checkinDocument(
              oneDriveDocumentDownloadUrl,
              objConRequired,
              selectedObjectConnections,
              docClass,
              docFormat,
              docTitle,
              uploadFiles,
              createViewCopy,
              uploadViewCopyCount,
              viewRequired,
              docReleaseStatus,
              oneDriveDeleteRequired,
              {
                retry: tokenRefreshPreActionInvoke.retry,
                actionType: REFRESH_TOKEN_ACTION.checkinNew,
                refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
              },
            );
            break;
          case REFRESH_TOKEN_ACTION.checkinNewViewCopy:
            if (
              docCheckinType === DOC_CHECK_IN_TYPE.checkinNew &&
              isValidPlayload(attachedDocInfo) === true &&
              attachedDocInfo !== INVALID_DOCUMENT
            ) {
              if (uploadViewCopyCount > 0) {
                const isBrowseSource = selectedDocument?.source === "browse";
                onCheckinViewCopy(
                  docTitle,
                  docClass,
                  attachedDocInfo.docNo,
                  attachedDocInfo.docSheet,
                  attachedDocInfo.docRev,
                  viewCopyFiles,
                  viewRequired,
                  docReleaseStatus,
                  docCheckinType,
                  !isBrowseSource && oneDriveDeleteRequired,
                  {
                    retry: tokenRefreshPreActionInvoke.retry,
                    actionType: REFRESH_TOKEN_ACTION.checkinNewViewCopy,
                    refreshResponse:
                      tokenRefreshPreActionInvoke.refreshResponse,
                  },
                );
              }
            }
            break;
          case REFRESH_TOKEN_ACTION.logicalUnitsLoad:
            onObjectConnectionsLoad(teamsContext, {
              retry: tokenRefreshPreActionInvoke.retry,
              actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
              refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
            });
            break;
          case REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad:
            getRecentObjects({
              retry: tokenRefreshPreActionInvoke.retry,
              actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
              refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
            });
            break;
          default:
            console.log(
              "No action for token refresh pre-action invoke: " +
                tokenRefreshPreActionInvoke.actionType,
            );
            break;
        }
      };
      run();
    }
  }, [tokenRefreshPreActionInvoke]);

  const _hierarchy = [
    {
      text: displayText.objectConnection,
      key: tabKey.checkinNewObjCon,
      parent: tabKey.checkinNewBasic,
    },
  ];

  const dataLoadingStatus = DATA_LOADING_STATUS;

  const [breadcrumbSubItms, setBreadcrumbSubItms] = useState(
    filterHierarchy(_hierarchy, tabKey.checkinNewObjCon, true),
  );

  const [tabIndexL4, setTabIndexL4] = useState(tabKey.checkinNewSelectedObj);
  const [nextButtonVisibility, setNextButtonVisibility] = useState(false);
  const [backButtonVisibility, setBackButtonVisibility] = useState(false);
  const [checkinButtonVisibility, setCheckinButtonVisibility] = useState(true);
  const [cancelButtonVisibility, setCancelButtonVisibility] = useState(false);
  const [dataLoad, setDataLoad] = useState(dataLoadingStatus.completed);
  const [
    recentObjectsAndObjSearchVisibility,
    setRecentObjectsAndObjSearchVisibility,
  ] = useState(false);
  const [selectedObjectsVisibility, setSelectedObjectsVisibility] =
    useState(true);
  const [
    backToSelectedObjsButtonVisibility,
    setBackToSelectedObjsButtonVisibility,
  ] = useState(false);
  const [isObjectPanelVisible, setObjectPanelVisible] = useState(false);

  const isCheckinBusy =
    isCheckinInProgress ||
    isUpdatingOneDrive ||
    isCheckinInLoadingState(docCheckinType, checkinDocumentProgress) ||
    (docCheckinType === DOC_CHECK_IN_TYPE.checkinNew && checkinLoading);

  const theme = useTheme();

  function onPovotItemClick(item) {
    setTabIndexL4(tabKey.checkinNewSelectedObj);
    setNextButtonVisibility(false);
    setCheckinButtonVisibility(true);
    setRecentObjectsAndObjSearchVisibility(false);
    setSelectedObjectsVisibility(true);
    setBackToSelectedObjsButtonVisibility(false);
  }

  function moveNext() {
    switch (checkinPageName) {
      case CHECKIN_NEW_PAGE_NAME.file:
      default:
        onCheckinPageNameChanged(CHECKIN_NEW_PAGE_NAME.basic);
        setBackButtonVisibility(false);
        setNextButtonVisibility(false);
        setCancelButtonVisibility(true);
        setCheckinButtonVisibility(true);
        setObjectPanelVisible(false);
        recentObjectsSelectedDecription !== ""
          ? onRecentObjectSelectedDescription("")
          : onRecentObjectSelectedDescription("");
        break;
      case CHECKIN_NEW_PAGE_NAME.basic:
        break;
    }
  }

  function moveBackToBasic() {
    onCheckinPageNameChanged(CHECKIN_NEW_PAGE_NAME.basic);
    setBackButtonVisibility(false);
    setNextButtonVisibility(false);
    setCancelButtonVisibility(true);
    setCheckinButtonVisibility(true);
    setObjectPanelVisible(false);
    moveToObjectConnection();
  }

  function moveToObjectConnection() {
    setObjectPanelVisible(false);
    setSelectedObjectsVisibility(true);
    setRecentObjectsAndObjSearchVisibility(false);
    setBackToSelectedObjsButtonVisibility(false);
    onRecentObjectSelectedDescription("");
  }

  function isButtonDisabled(buttonType) {
    switch (buttonType) {
      case displayText.next:
      default:
        switch (checkinPageName) {
          case CHECKIN_NEW_PAGE_NAME.file:
            return true;

          case CHECKIN_NEW_PAGE_NAME.basic:
            if (docTitle !== "" && docClass !== "" && fileCount > 0) {
              if (isFileExtValid(docFileTypeList, uploadFiles) === false) {
                return true;
              }

              if (viewRequired === true) {
                if (uploadViewCopyCount === 0 && createViewCopy !== true) {
                  return true;
                }
              }

              if (formatRequired === true && !docFormat) {
                return true;
              }

              return false;
            }
            return true;

          default:
            return true;
        }

      case displayText.checkin:
        if (
          checkinPageName === CHECKIN_NEW_PAGE_NAME.basic ||
          checkinPageName === CHECKIN_NEW_PAGE_NAME.connection
        ) {
          if (docTitle !== "" && docClass !== "" && fileCount > 0) {
            if (isFileExtValid(docFileTypeList, uploadFiles) === false) {
              return true;
            }

            let disableButton =
              viewRequired === true
                ? uploadViewCopyCount > 0 || createViewCopy === true
                  ? false
                  : true
                : false;

            if (formatRequired === true && !docFormat) {
              disableButton = true;
            }

            if (
              objConRequired === true &&
              (!selectedObjectConnections ||
                selectedObjectConnections.length === 0)
            ) {
              disableButton = true;
            }

            if (
              isCheckinBusy ||
              ((objectConnectionDataLoad !== DATA_LOAD_STATUS.completed ||
                isTreeDataLoading) &&
                !isIFSAutopopulated)
            ) {
              disableButton = true;
            }

            return disableButton;
          }
        }
        return true;
    }
  }

  const checkinDocument = async (
    url,
    objConRequired,
    selectedObjectConnections,
    docClass,
    docFormat,
    docTitle,
    uploadFileList,
    createViewCopy,
    uploadViewCopyCount,
    viewRequired,
    docReleaseStatus,
    oneDriveDeleteRequired,
    tokenRefreshPreAction = {
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.checkinNew,
      refreshResponse: RESPONSE_TYPE.success,
    },
  ) => {
    if (isCheckinBusy) return;
    if (
      !onCheckinValidation(objConRequired, selectedObjectConnections, docClass)
    ) {
      return;
    }
    // `fetchOneDriveDownloadUrl` from useOneDrive dispatches a thunk; the resolved value
    // is the Redux *action* ({ type, payload, meta }), not the URL string. Using it as
    // `fileUrl` caused API 400: fileUrl could not convert to string.
    const urlFromCaller =
      typeof url === "string" && url.trim() !== "" ? url.trim() : null;
    let urlToUse = urlFromCaller;
    const isBrowseSource = selectedDocument?.source === "browse";
    
    const browseFileBlob =
      (uploadFileList?.[0]?.file instanceof Blob && uploadFileList[0].file) ||
      (selectedDocument?.file instanceof Blob && selectedDocument.file) ||
      null;
    const existingBase64 =
      (typeof uploadFileList?.[0]?.base64str === "string" &&
        uploadFileList[0].base64str.trim()) ||
      (typeof selectedDocument?.base64str === "string" &&
        selectedDocument.base64str.trim()) ||
      "";
    /** True when we already showed check-in progress before awaiting download-URL fetch. */
    let showedCheckinProgressForUrlFetch = false;
    // Browse picks local files and already has base64 — never treat the synthetic
    // browse id (e.g. "empty2 (1).docx-0-11325") as a Graph drive item id.
    if (!urlToUse && !isBrowseSource && !existingBase64 && !browseFileBlob) {
      // Check-in New list sometimes returns no `microsoftGraphDownloadUrl`.
      // In that case we generate a real OneDrive download URL using our API.
      const selectedDocId = selectedDocument?.key ?? null;
      const tenantId = teamsContext?.user?.tenant?.id ?? "";
      const { groupId, channelId, fileRoot } = resolveGroupId(teamsContext);

      if (!selectedDocId || !tenantId || !fileRoot) {
        onCheckinNewErrorTrigger(CHECKIN_NEW_TIME_OUT);
        return;
      }

      // Same feedback as check-in start, so the user sees activity during the URL API round-trip.
      setIsCheckinInProgress(true);
      onDocCheckinTypeChange(DOC_CHECK_IN_TYPE.checkinNew);
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoad);
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoading);
      showedCheckinProgressForUrlFetch = true;

      try {
        // useOneDrive.fetchOneDriveDownloadUrl expects positional args, not an object.
        // Signature: (teamId, channelId, tenantId, documentId, fileRoot, operationType)
        const downloadAction = await fetchOneDriveDownloadUrl(
          groupId,
          channelId ?? "",
          tenantId,
          selectedDocId,
          fileRoot,
          DOC_CHECK_IN_TYPE.checkinNew,
          { __toast: { suppressToast: true } },
        );
        urlToUse =
          downloadAction?.meta?.requestStatus === "fulfilled" &&
          typeof downloadAction.payload === "string"
            ? downloadAction.payload
            : null;
      } catch (_e) {
        urlToUse = null;
      }

      if (!urlToUse) {
        setIsCheckinInProgress(false);
        onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
        onCheckinNewErrorTrigger(CHECKIN_NEW_TIME_OUT);
        return;
      }
    }
    if (!urlToUse && !existingBase64 && !browseFileBlob) {
      onCheckinNewErrorTrigger(CHECKIN_NEW_TIME_OUT);
      return;
    }
    try {
      if (!showedCheckinProgressForUrlFetch) {
        setIsCheckinInProgress(true);
        onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoad);
        onDocCheckinTypeChange(DOC_CHECK_IN_TYPE.checkinNew);
      }
      let checkinFile = uploadFileList[0];
      const fileName = checkinFile?.name ?? "";
      const fileExtension = fileName.includes(".")
        ? String(fileName.split(".").pop() ?? "").toLowerCase()
        : "";
      const shouldUploadFileDataFromUrl = ["txt", "html", "htm"].includes(
        fileExtension,
      );
      let base64str = existingBase64;
      if (!base64str && !browseFileBlob && shouldUploadFileDataFromUrl && urlToUse) {
        const base64FromUrl = await getBase64FromUrl(urlToUse);
        if (typeof base64FromUrl === "string") {
          const commaIndex = base64FromUrl.indexOf(",");
          base64str =
            commaIndex >= 0
              ? base64FromUrl.substring(commaIndex + 1)
              : base64FromUrl;
        }
      }
      var allFiles = [];
      let fileInfo = {
        name: checkinFile.name,
        type: checkinFile.type,
       
        size: "0 kB",
        base64: "",
        base64str,
        file: browseFileBlob || checkinFile?.file || null,
        source: isBrowseSource ? "browse" : checkinFile?.source,
      };
      allFiles.push(fileInfo);
      onFileDataChange(allFiles, allFiles.length);

      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoading);
      const payload = {
        docTitle,
        docClass,
        docFormat,
        formatRequired,
        uploadFiles: allFiles,
        fileUrl: base64str || browseFileBlob ? undefined : urlToUse,
        createViewCopy,
        selectedObjects: selectedObjectConnections,
        uploadViewCopyCount,
        viewRequired,
        docReleaseStatus,
        oneDriveDeleteRequired,
        restrictedAccess,
        tokenRefreshPreAction,
        __toast: { suppressToast: true },
      };
      const result = await submitCheckin(payload);
      if (result?.payload?.data != null) {
        const attachedData = result.payload.data;
        dispatch({
          type: ON_ATTACHED_DOC_INFO,
          payload: attachedData,
        });
        // Check-in API call is complete; keep dialog open with OneDrive update message
        // while the OneDrive metadata update / delete operations finish (OneDrive files only).
        setIsCheckinInProgress(false);
        if (!isBrowseSource) {
          setIsUpdatingOneDrive(true);
        }

        // Update metadata on the original OneDrive file after successful check-in
        // (only when the file is NOT scheduled for deletion and not a local Browse file)
        if (!isBrowseSource && oneDriveDocumentId && !oneDriveDeleteRequired) {
          const { groupId: metaGroupId, fileRoot: metaFileRoot } =
            resolveGroupId(teamsContext);
          const metaTenantId = teamsContext?.user?.tenant?.id ?? "";
          const metaWebUser =
            teamsContext?.user?.userPrincipalName ||
            teamsContext?.user?.displayName ||
            "";
          try {
            const metaUserAuth = new UserAuth();
            const metaTokenResponse = await metaUserAuth.getUserToken(true);
            const metaAccessToken = metaTokenResponse?.accessToken ?? null;
            if (metaAccessToken) {
              await updateOneDriveMetadata(metaAccessToken, {
                userId: teamsContext?.user?.id ?? "",
                tenantId: metaTenantId,
                teamId: metaGroupId || "",
                channelId: teamsContext?.channel?.id || "",
                oneDriveRoot: metaFileRoot || "",
                documentId: String(oneDriveDocumentId),
                webUser: metaWebUser,
                metadata: {
                  docClass: attachedData.docClass,
                  docNo: attachedData.docNo,
                  docRev: attachedData.docRev,
                  docSheet: attachedData.docSheet,
                  formatSize: docFormat,
                  state: docReleaseStatus || "",
                  fileState: "CheckedIn",
                  owner: metaWebUser,
                  lastActionDate: new Date().toISOString(),
                  // Keep download flag so Working Documents icon stays download,
                  // not checkout, when IFS FileState is still Checked Out.
                  isDownloaded: isWorkingDocumentDownloaded(selectedDocument),
                },
              });
            }
          } catch (_metaErr) {
            // Metadata update failure should not block the check-in success flow.
          }
        }

        if (uploadViewCopyCount > 0) {
          // View copy flow continues asynchronously via useEffect —
          // do NOT reset isCheckinInProgress here; the useEffect handlers
          // (onCompletion / onDeleting / onError) will reset it.
          // Browse files are local — never request OneDrive delete after view copy.
          onCheckinViewCopy(
            docTitle,
            docClass,
            attachedData.docNo,
            attachedData.docSheet,
            attachedData.docRev,
            viewCopyFiles,
            viewRequired,
            docReleaseStatus,
            docCheckinType,
            !isBrowseSource && oneDriveDeleteRequired,
            {
              retry: 0,
              actionType: REFRESH_TOKEN_ACTION.checkinNewViewCopy,
              refreshResponse: RESPONSE_TYPE.none,
            },
            REFRESH_TOKEN_ACTION.checkinNewViewCopy,
          );
          // Refresh the channel/OneDrive documents list so the saved metadata is visible
          // when the user returns to the "Channel Documents" tab after view-copy completes.
          dispatch(requestRefreshCheckInNewList());
          return; // skip the finally reset
        } else {
          // Update OneDrive document metadata after successful check-in
          // (skip for local Browse files — their key is not a Graph item id).
          if (!isBrowseSource) {
            try {
              const userAuthToken = (await new UserAuth().getUserToken(true))
                ?.accessToken;
              if (userAuthToken && selectedDocument?.key) {
                const { groupId: metaGroupId, fileRoot: metaFileRoot } =
                  resolveGroupId(teamsContext);
                const metaWebUser = teamsContext?.user?.userPrincipalName ?? "";
                await updateOneDriveMetadata(userAuthToken, {
                  userId: teamsContext?.user?.id ?? "",
                  tenantId: teamsContext?.user?.tenant?.id ?? "",
                  teamId: metaGroupId ?? "",
                  channelId: teamsContext?.channel?.id ?? "",
                  oneDriveRoot: metaFileRoot ?? "",
                  documentId: selectedDocument.key,
                  webUser: metaWebUser,
                  metadata: {
                    docClass: attachedData.docClass ?? docClass,
                    docNo: attachedData.docNo ?? "",
                    docSheet: attachedData.docSheet ?? "1",
                    docRev: attachedData.docRev ?? "",
                    formatSize: docFormat,
                    state: docReleaseStatus || "",
                    fileState: "CheckedIn",
                    owner: metaWebUser,
                    lastActionDate: new Date().toISOString().split("T")[0],
                    // Keep download flag so Working Documents icon stays download,
                    // not checkout, when IFS FileState is still Checked Out.
                    isDownloaded: isWorkingDocumentDownloaded(selectedDocument),
                  },
                });
              }
            } catch (_err) {
              logger.warn(
                "updateOneDriveMetadata failed after check-in, continuing",
                _err,
                "CHECKIN",
              );
            }
          }

          const shouldDeleteFromOneDrive =
            !isBrowseSource && oneDriveDeleteRequired;
          const deleteDocumentId = selectedDocument?.key || oneDriveDocumentId;
          if (shouldDeleteFromOneDrive && deleteDocumentId) {
            const { groupId: deleteGroupId, fileRoot: deleteFileRoot } =
              resolveGroupId(teamsContext);
            const effectiveDeleteGroupId =
              deleteGroupId || requestGroupId || "";
            const deleteMembershipType =
              teamsContext?.channel?.membershipType || "Regular";
            const deleteChannelId =
              teamsContext?.channel?.id || requestChannelId || "";
            try {
              await deleteOneDriveDocument(
                deleteMembershipType,
                deleteDocumentId,
                effectiveDeleteGroupId,
                null,
                DOC_CHECK_IN_TYPE.checkinNew,
                deleteFileRoot,
                deleteChannelId,
              );
            } catch (_e) {
              // Deletion failure should not block the check-in success flow.
            }

            try {
              await refreshWorkingDocuments(50);
            } catch (_e) {}

            onSetDocumentToDeleteOneDrive({
              checkinSuccess: false,
              id: null,
              deleteRequired: false,
              checkinType: null,
            });
          }

          onAllowSelectionChange(false);
          setIsUpdatingOneDrive(false);
          // Refresh the channel/OneDrive documents list so the saved metadata is visible
          // when the user returns to the "Channel Documents" tab.
          dispatch(requestRefreshCheckInNewList());
          onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
          onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
          onClear(false);
          // If the user dismissed the progress popup, finish silently: keep the
          // success toast but don't pull them to All Documents.
          if (!progressDialogDismissedRef.current) {
            navigate(APP_ROUTES.allDocuments);
          }
          const successDocumentTitle =
            attachedData?.title || docTitle || "Document";
          setTimeout(() => {
            onSuccessMessageInvoke({
              type: SUCCESS_TYPE,
              title: "Check-in completed",
              message: `${successDocumentTitle} checked in successfully!`,
            });
          }, 0);
        }
      }
    } catch (err) {
      onAllowSelectionChange(false);
      onCancel();
      setIsCheckinInProgress(false);
      setIsUpdatingOneDrive(false);
      dispatch(requestRefreshCheckInNewList());
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
    }
  };

  const getBase64FromUrl = async (url) => {
    setDataLoad(dataLoadingStatus.loading);
    try {
      const data = await fetch(url);
      const blob = await data.blob();
      if (blob != null) {
        setDataLoad(dataLoadingStatus.completed);
      } else {
        setDataLoad(dataLoadingStatus.nodata);
      }

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result;
          resolve(base64data);
        };
      });
    } catch (error) {
      setDataLoad(dataLoadingStatus.error);
    }
  };

  const handleCancelClick = () => {
    onAllowSelectionChange(false);
    onCancel();
  };

  const handleCheckinClick = async () => {
    progressDialogDismissedRef.current = false;
    setProgressDialogDismissed(false);
    setShowFieldValidationErrors(true);
    if (checkinPageName === CHECKIN_NEW_PAGE_NAME.basic) {
      if (!docTitle?.trim() || !docClass?.trim()) {
        return;
      }
      if (isFileExtValid(docFileTypeList, uploadFiles) === false) {
        return;
      }
      if (
        !onBasicValidation(
          viewRequired,
          formatRequired,
          uploadViewCopyCount,
          createViewCopy,
          docFormat,
          docClass,
        )
      ) {
        return;
      }
    }
    await checkinDocument(
      oneDriveDocumentDownloadUrl,
      objConRequired,
      selectedObjectConnections,
      docClass,
      docFormat,
      docTitle,
      uploadFiles,
      createViewCopy,
      uploadViewCopyCount,
      viewRequired,
      docReleaseStatus,
      oneDriveDeleteRequired,
    );
  };

  function clearCheckin() {
    logger.debug(
      "CheckInNew clearCheckin start",
      {
        selectedObjectConnectionsCount: Array.isArray(selectedObjectConnections)
          ? selectedObjectConnections.length
          : null,
        docClassFormatListIsArray: Array.isArray(docClassFormatList),
        checkinPageName,
      },
      "CHECKIN",
    );
    onDocTitleChange("");
    onDocClassFormatChange("", "", "N", "N", "N", null, 0);
    onFileUploadChange(null, 0, 0, false);
    onViewCopyUploadChange(null, 0, 0, false, null);
    onDocReleaseChange(false);
    onCreateViewCopyDisable(true);
    onCreateViewCopySelect(false, { programmatic: true });
    onObjectConnectionDataLoad(DATA_LOAD_STATUS.onStart);
    cleanAll();
    onDocClassFormatListChange(resetHierarchy(docClassFormatList));
    onRecentObjectConnectionChanged([]);
    onRecentObjectDataLoadStatusChanged(DOCUMENT_LOAD_STATUS.onStart);
    if (
      selectedObjectConnections !== undefined &&
      selectedObjectConnections !== null &&
      selectedObjectConnections.length > 0
    ) {
      onSelectedObjectConnectionChanged([], []);
    }
    onSelectedKeyLuNameChange("");
    onRecentObjectSelectedDescription("");
    logger.debug("CheckInNew clearCheckin complete", null, "CHECKIN");
  }

  const handleButtonCheckin = () => {
    const fromWorkingDocuments =
      location.state?.from === APP_ROUTES.workingDocuments;
    const fromWorkingDocumentCheckInHub =
      location.pathname === APP_ROUTES.workingDocumentCheckIn;
    const fromAllDocuments = location.state?.from === APP_ROUTES.allDocuments;

    const isInCheckInForm =
      location.pathname === APP_ROUTES.checkInNewForm ||
      location.pathname === APP_ROUTES.checkInNewRevision ||
      location.pathname === APP_ROUTES.workingDocumentCheckIn;

    if (fromWorkingDocuments || fromWorkingDocumentCheckInHub) {
      tools.onSelectedPageChange(PAGE_NAME.home);
      navigate(APP_ROUTES.workingDocuments, {
        state: { [NAV_LOCATION_STATE.skipWorkingDocumentsRefresh]: true },
      });
    } else if (fromAllDocuments) {
      navigate(APP_ROUTES.allDocuments);
    } else if (isInCheckInForm) {
      navigate(APP_ROUTES.checkInNew);
    } else {
      navigate(APP_ROUTES.checkInNew);
    }
  };
  // Close (X) on the check-in progress popup: only dismisses the popup and
  // returns the user to the originating list. The background check-in keeps
  // running and finishes silently — the post-success navigation is skipped so
  // the user is not pulled to All Documents (the success toast still shows).
  const handleProgressDialogClose = () => {
    progressDialogDismissedRef.current = true;
    setProgressDialogDismissed(true);
    handleButtonCheckin();
  };

  function onCancel() {
    logger.debug(
      "CheckInNew onCancel invoked",
      {
        checkinDocumentProgress,
        docCheckinType,
        checkinPageName,
      },
      "CHECKIN",
    );
    // Do not set OneDrive/IFS load flags here — user cancelled; document lists stay as-is.
    onClear();
  }

  function onClear(shouldNavigate = true) {
    logger.debug("CheckInNew onClear start", { checkinPageName }, "CHECKIN");
    clearCheckin();
    onCheckinPageNameChanged(CHECKIN_NEW_PAGE_NAME.file);
    setBackButtonVisibility(false);
    setNextButtonVisibility(true);
    setCheckinButtonVisibility(false);
    setObjectPanelVisible(false);
    onRecentObjectSelectedDescription("");
    if (shouldNavigate) {
      handleButtonCheckin();
    }
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
    onCheckinProgressChange(CHECKIN_PROCESS_TYPE.start);
    logger.debug("CheckInNew onClear complete", null, "CHECKIN");
  }

  return (
    <>
      <Stack
        enableScopedSelectors
        horizontal
        styles={{
          root: {
            width: "100%",
            height: "100%",
            flex: "1 1 auto",
            minHeight: 0,
            alignItems: "stretch",
          },
        }}
        tokens={{ childrenGap: 0, padding: 0 }}
      >
        <Stack.Item
          className="ms-hiddenLgDown"
          disableShrink
          styles={{
            root: {
              height: "100%",
              borderStyle: "solid",
              borderWidth: "0px 1px 0px 0px",
              borderColor: theme.colors.Addovation20Grey,
            },
          }}
        ></Stack.Item>

        <Stack.Item
          grow
          style={{
            height: "100%",
          }}
        ></Stack.Item>

        <Stack.Item
          grow
          style={{
            height: "100%",
          }}
        ></Stack.Item>
        {isCheckinBusy && !progressDialogDismissed && (
          <ReusableDialog
            isOpen
            onClose={() => {}}
            showHeader={false}
            /* Show the dismiss (X) on the check-in progress popup, but not while
               the OneDrive document is being updated. */
            showCloseIcon={!(isUpdatingOneDrive && !isCheckinInProgress)}
            onCloseIcon={handleProgressDialogClose}
            closeIconTooltip="Check-in will still continue after closing"
            isBlocking
            centerContent
            dialogMinHeight={100}
            dialogMinWidth="300px"
            bodyMinHeight={60}
          >
            <MessageBoxContent
              isLoading
              loadingText={
                isUpdatingOneDrive && !isCheckinInProgress
                  ? ONEDRIVE_UPDATE_MESSAGE
                  : CHECKIN_LOADING_MESSAGE
              }
              showOkButton={false}
            />
          </ReusableDialog>
        )}
        <div
          style={{
            ...panelStyle.panelStyle.root,
            flex: "1 1 auto",
            minHeight: 0,
            minWidth: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              position: "relative",
              flexGrow: 1,
              minHeight: 0,
              overflow: "hidden",
              background: theme.colors.mainAreaBackgroundColor,
            }}
          >
            <div
              style={{
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarColor: `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarBackground}`,
                paddingRight: 5,
                boxSizing: "border-box",
              }}
            >
              {checkinPageName === CHECKIN_NEW_PAGE_NAME.basic && (
                <>
                  <DocumentBasicItem
                    onTreeDataLoadingChange={setIsTreeDataLoading}
                    objectConnectionTabKey={tabIndexL4}
                    onObjectConnectionTabKeyChange={(key) => {
                      setTabIndexL4(key);
                      setBreadcrumbSubItms(
                        filterHierarchy(_hierarchy, key, true),
                      );
                    }}
                    tabKey={tabKey}
                    showFieldValidation={
                      showFieldValidationErrors && !isIFSAutopopulating
                    }
                    isAutopopulating={isIFSAutopopulating}
                  />
                  {!!selectedDocument &&
                    selectedDocument.source !== "browse" && (
                      <DeleteOneDriveCheckbox
                        checked={Boolean(oneDriveDeleteRequired)}
                        onChange={handleDeleteOneDriveChange}
                      />
                    )}
                </>
              )}
            </div>
          </div>
          {!isObjectPanelVisible && (
            <div style={panelStyle.buttonPanelStyle.root}>
              <Stack
                enableScopedSelectors
                horizontal
                tokens={{ childrenGap: 10 }}
                style={{ gap: "10px" }}
              >
                <CheckInNewFooterActions
                  backButtonVisibility={backButtonVisibility}
                  cancelButtonVisibility={cancelButtonVisibility}
                  nextButtonVisibility={nextButtonVisibility}
                  checkinButtonVisibility={checkinButtonVisibility}
                  isTreeDataLoading={isTreeDataLoading}
                  styleTheme={styleTheme}
                  displayText={displayText}
                  isButtonDisabled={isButtonDisabled}
                  onBack={moveBackToBasic}
                  onCancel={handleCancelClick}
                  onMoveNext={moveNext}
                  onCheckin={handleCheckinClick}
                />
              </Stack>
            </div>
          )}
          <MessageBox />
          <SuccessToastBox />
          <ErrorDailog />
          <SuccessDialog />
        </div>
      </Stack>
    </>
  );
};

export default CheckInNew;
